import React, { Fragment, memo, useCallback, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Form, Formik } from 'formik'
import { Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useSelector } from 'react-redux'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { ConfirmActionModal } from 'src/components/shared/ConfirmActionModal'
import { DataRow } from 'src/components/shared/DataRow'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { AdminAssignInternalUserRole, AdminSetInternalUserStatus } from 'src/constants/actionTypes'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useHasPermission } from 'src/hooks/useHasPermission'
import { useInternalPermissions } from 'src/hooks/useInternalPermissions'
import type { InternalAccessUser } from 'src/interfaces/internalAccess'
import {
  getInternalRoles,
  getInternalUserSaving,
  getInternalUsers,
} from 'src/selectors/internalAccess'
import { getInternalIdentity } from 'src/selectors/internalAuth'
import { formatDateTime, parseFlexibleDateTime } from 'src/utils/grid'
import {
  buildAssignableRoleOptions,
  buildInternalUserName,
  isProtectedUserLockedFor,
  resolveInitialRoleId,
} from 'src/utils/internalAccess/internalUsers'
import { InternalUserStatusChip } from './InternalUserStatusChip'

const FORM_ID = 'internal-user-role-form'

interface Props {
  /** `null` closes the modal. */
  userId: string | null
  onClose(): void
}

interface RoleFormValues {
  roleId: string
}

const formatTimestamp = (value: unknown): string => {
  return formatDateTime(parseFlexibleDateTime(value))
}

/**
 * Reads the user BY ID from the store rather than holding a row copy, so the details are never a
 * stale snapshot. A successful role or status change closes the modal (the table row already shows
 * the reducer's swapped-in user); a failure keeps it open behind the error toast.
 *
 * `Formik` wraps `CommonModal` so the footer's submit — rendered outside `<Form>` — still reads
 * the form through context (same shape as `UpdateRetryTemplateModal`).
 */
const InternalUserModalComponent = ({ userId, onClose }: Props) => {
  const users = useSelector(getInternalUsers)
  const roles = useSelector(getInternalRoles)
  const identity = useSelector(getInternalIdentity)
  const grantable = useInternalPermissions()
  const isSaving = useSelector(getInternalUserSaving)
  const canManage = useHasPermission(InternalPermission.InternalUsersManage)
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const snackbar = useSnackbar()

  const [isDisableConfirmOpen, setIsDisableConfirmOpen] = useState<boolean>(false)

  const user = useMemo(() => {
    return (
      users.find((candidate) => {
        return candidate.id === userId
      }) ?? null
    )
  }, [users, userId])

  const roleOptions = useMemo(() => {
    return buildAssignableRoleOptions(roles, grantable)
  }, [roles, grantable])

  const initialValues = useMemo<RoleFormValues>(() => {
    return { roleId: user ? resolveInitialRoleId(user, roles) : '' }
  }, [user, roles])

  // The backend refuses both with a 403; disabling the controls just avoids offering them.
  const isSelf = Boolean(user && identity && user.id === identity.internalUserId)
  const isProtectedLocked = Boolean(
    user && isProtectedUserLockedFor(user, roles, identity?.roleName ?? null),
  )
  const canEdit = canManage && !isSelf && !isProtectedLocked

  const handleSubmit = useCallback(
    async (values: RoleFormValues) => {
      if (!user || !values.roleId) {
        return
      }

      const result = await asyncDispatchWithErrorToast<InternalAccessUser>(
        AdminAssignInternalUserRole.request({ userId: user.id, roleId: values.roleId }),
      )

      if (!result) {
        return
      }

      snackbar?.enqueueSnackbar('Role updated', { variant: 'success' })
      onClose()
    },
    [asyncDispatchWithErrorToast, onClose, snackbar, user],
  )

  const setStatus = useCallback(
    async (disabled: boolean) => {
      if (!user) {
        return
      }

      const result = await asyncDispatchWithErrorToast<InternalAccessUser>(
        AdminSetInternalUserStatus.request({ userId: user.id, disabled }),
      )

      setIsDisableConfirmOpen(false)

      if (!result) {
        return
      }

      snackbar?.enqueueSnackbar(disabled ? 'User disabled' : 'User enabled', {
        variant: 'success',
      })
      onClose()
    },
    [asyncDispatchWithErrorToast, onClose, snackbar, user],
  )

  const handleStatusClick = useCallback(() => {
    if (!user) {
      return
    }

    if (user.disabled) {
      setStatus(false)

      return
    }

    setIsDisableConfirmOpen(true)
  }, [setStatus, user])

  const handleConfirmDisable = useCallback(() => {
    setStatus(true)
  }, [setStatus])

  const handleCloseConfirm = useCallback(() => {
    setIsDisableConfirmOpen(false)
  }, [])

  if (!user) {
    // Unmounting resets Formik, so the next open starts from that user's values.
    return null
  }

  const userName = buildInternalUserName(user)

  return (
    <Formik<RoleFormValues>
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ values }) => {
        const isRoleUnchanged = !values.roleId || values.roleId === user.roleId

        return (
          <Fragment>
            <CommonModal
              isOpen
              title={userName}
              onClose={onClose}
              className={tw`max-w-[560px]`}
              closeButtonTestId="internal-user-modal-close"
              buttons={
                <Fragment>
                  {canManage ? (
                    <Button
                      size="small"
                      color={user.disabled ? 'green' : 'red'}
                      onClick={handleStatusClick}
                      disabled={!canEdit || isSaving}
                      data-testid="internal-user-status-button"
                    >
                      {user.disabled ? 'Enable user' : 'Disable user'}
                    </Button>
                  ) : null}
                  <Button
                    size="small"
                    color="grey"
                    onClick={onClose}
                    data-testid="internal-user-close-button"
                  >
                    Close
                  </Button>
                  {canManage ? (
                    <Button
                      size="small"
                      color="dark"
                      type="submit"
                      form={FORM_ID}
                      loading={isSaving}
                      disabled={!canEdit || isSaving || isRoleUnchanged}
                      data-testid="internal-user-save-role-button"
                    >
                      Save role
                    </Button>
                  ) : null}
                </Fragment>
              }
            >
              <Form id={FORM_ID} data-testid="internal-user-modal">
                <div css={tw`flex flex-col gap-4`}>
                  <div css={tw`flex flex-col gap-2`}>
                    <DataRow title="Email" text={user.email} />
                    <DataRow title="Role" text={user.roleName ?? 'No role'} />
                    <DataRow
                      title="Status"
                      component={<InternalUserStatusChip isDisabled={user.disabled} />}
                    />
                    {user.disabled ? (
                      <DataRow title="Disabled at" text={formatTimestamp(user.disabledAt)} />
                    ) : null}
                    <DataRow title="Last active" text={formatTimestamp(user.lastActiveAt)} />
                    <DataRow title="Created" text={formatTimestamp(user.createdAt)} />
                  </div>
                  {canManage ? (
                    <FormikSelect<RoleFormValues>
                      name="roleId"
                      label="Change role"
                      placeholder="Select a new role"
                      options={roleOptions}
                      disabled={!canEdit || isSaving}
                      data-testid="internal-user-role-select"
                    />
                  ) : null}
                  {canManage && canEdit && roleOptions.length === 0 ? (
                    <Typography variant="body2" css={tw`text-xs text-shades-500`}>
                      No roles you can assign.
                    </Typography>
                  ) : null}
                  {canManage && isSelf ? (
                    <Typography variant="body2" css={tw`text-xs text-shades-500`}>
                      You can&apos;t change your own role or disable yourself.
                    </Typography>
                  ) : null}
                  {canManage && !isSelf && isProtectedLocked ? (
                    <Typography variant="body2" css={tw`text-xs text-shades-500`}>
                      Only a super admin can change a super admin&apos;s role or disable them.
                    </Typography>
                  ) : null}
                </div>
              </Form>
            </CommonModal>
            <ConfirmActionModal
              isOpen={isDisableConfirmOpen}
              title={`Disable ${userName}?`}
              description="They lose admin access immediately. You can enable them again later."
              confirmLabel="Disable user"
              isDestructive
              isSubmitting={isSaving}
              onConfirm={handleConfirmDisable}
              onClose={handleCloseConfirm}
              confirmDataTestId="internal-user-disable-confirm"
            />
          </Fragment>
        )
      }}
    </Formik>
  )
}

export const InternalUserModal = memo(InternalUserModalComponent)
